import { Warehouse } from '../../domain/warehouse.model';
import { Component } from '@angular/core';
import { ItemService } from '../../services/item.service';
import { Item, ItemMovement, ItemSearchParams, ItemStatus } from '../../domain/inventory.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AccountService } from '../../services/account.service';
import { Account, PageableAccount } from '../../domain/account.model';
import { debounceTime, Subject } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { AccountDropdown } from '../../domain/accountDropdown.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ColumnConfig } from '../../domain/column_config.model';
import { ColumnPreferenceService } from '../../services/column-preference-service.service';
import { ExportExcelService } from '../../services/export-excel.service';

@Component({
    selector: 'app-articles-inventory',
    templateUrl: './articles-inventory.component.html',
    styleUrl: './articles-inventory.component.scss',
    providers: [MessageService, ConfirmationService],
    standalone: false
})

export class ArticlesInventoryComponent {
  items: Item[] = [];
  accounts: any[] = [];
  userAccount: Account = {};
  totalRecords: number = 0;
  loading: boolean = true;
  accountLoading: boolean = false;
  filters: any = {};
  statuses = [
    { label: 'RECIBIDO', value: 'RECIBIDO' },
    { label: 'PALETIZADO', value: 'PALETIZADO' },
    { label: 'COMPLETADO', value: 'COMPLETADO' },
    { label: 'ELIMINADO', value: 'ELIMINADO' }
  ];
  warehouses: Warehouse[] = [
    {
      id: 1,
      name: 'Bodega Zapopan'
    }
  ];
  itemStatus = ItemStatus;
  totalStock: number = 0;
  totalUnits: number = 0;

  //Account dropdown
  lastQuery: string = '';
  searchAccountSubject = new Subject<string>();
  searchAccountQuery: string = '';

  //Search form
  searchForm: FormGroup = this.fb.group({
    warehouse: [''],
    accountId: [null],
    code: [null],
    serialNumber: [null],
    description: [null],
    palletCode: [null],
    status: [null],
    creationDateFrom: [null],
    creationDateTo: [null],
  });
  tableRows: number = 30;
  firstPage = 0;

  //Actions variables
  selectedItem: any = null;

  // History dialog
  historyDialogVisible = false;
  movements: ItemMovement[] = [];

  //Column Toggle
  ALL_COLUMNS: ColumnConfig[] = [
    { field: 'itemId', header: '#', type: 'number', alwaysVisible: true },
    { field: 'code', header: 'Código de Artículo', type: 'string', sortable: true },
    { field: 'serialNumber', header: 'SKU', type: 'string' },
    { field: 'description', header: 'Descripción', type: 'string' },
    { field: 'pallet.qrCode', header: 'Palet', type: 'string' },
    { field: 'initialQuantity', header: 'Recibidos', type: 'number', defaultHidden: true },
    { field: 'quantity', header: 'En Stock', type: 'number' },
    { field: 'account.name', header: 'Cuenta', type: 'string' },
    { field: 'status', header: 'Estado', type: 'tag' },
    { field: 'creationDate', header: 'Creado', type: 'date', sortable: true },
    { field: 'updateDate', header: 'Actualizado', type: 'date', sortable: true },
    { field: 'releaseDate', header: 'Salida', type: 'date', sortable: true },
    { field: 'actions', header: 'Acciones', type: 'actions', alwaysVisible: true },
  ];

  ROLE_COLUMNS: Record<string, string[]> = {
    BILD_ADMIN: ['itemId', 'code', 'serialNumber', 'description', 'pallet.qrCode', 'initialQuantity', 'quantity', 'account.name', 'status', 'creationDate', 'updateDate', 'releaseDate', 'actions'],
    BILD_OPERADOR: ['itemId', 'code', 'description', 'pallet.qrCode', 'quantity', 'status', 'creationDate', 'releaseDate', 'actions'],
    BILD_ENCARGADO: ['itemId', 'code', 'description', 'pallet.qrCode', 'quantity', 'status', 'creationDate', 'releaseDate', 'actions'],
    BILD_VENTAS: ['itemId', 'code', 'description', 'quantity', 'status', 'creationDate', 'releaseDate', 'actions'],
    CLIENTE_ADMIN: ['itemId', 'code', 'serialNumber', 'pallet.qrCode', 'description', 'quantity', 'status', 'creationDate', 'updateDate', 'releaseDate', 'actions'],
    CLIENTE_OPERADOR: ['itemId', 'code', 'serialNumber', 'description', 'pallet.qrCode', 'quantity', 'status', 'creationDate', 'updateDate', 'releaseDate', 'actions'],
  };

  columnsToggleList: ColumnConfig[] = [];   // Columnas del rol (para el overlay)
  appliedTableColumns: ColumnConfig[] = [];  // Columnas activas en la tabla
  currentRole: string;
  currentUserId: string;
  searchTerm: string = '';
  selectAll: boolean = false;

  constructor(private itemService: ItemService, private fb: FormBuilder, private accountService: AccountService,
    private authService: AuthService, private confirmationService: ConfirmationService, private messageService: MessageService,
    private colPrefService: ColumnPreferenceService, private exportService: ExportExcelService) {
    this.currentRole = this.authService.getUserRole();
    this.currentUserId = this.authService.getUserDetails()?.userId;
  }

  ngOnInit(): void {
    this.searchForm.patchValue({ warehouse: this.warehouses[0].name });
    const savedRows = localStorage.getItem('rowsPerPage');
    if (savedRows) {
      this.tableRows = +savedRows;
    }
    this.fetchInitialData();
    this.searchAccountSubject.pipe(debounceTime(800)).subscribe(searchText => {
      this.searchAccountQuery = searchText;
      this.getAccounts();
    });
    this.initColumns();
  }

  get hasAccountAccess(): boolean {
    return this.authService.canAccessAction('articlesInventory', 'accountSelection');
  }

  get hasEditAccess(): boolean {
    return this.authService.canAccessAction('articlesInventory', 'edit');
  }

  get hasDeleteAccess(): boolean {
    return this.authService.canAccessAction('articlesInventory', 'delete');
  }

  search() {
    this.loadItems({ first: 0, rows: this.tableRows });
  }

  loadAccounts(event: any) {
    this.searchAccountSubject.next(event.filter || '');
  }

  fetchInitialData() {
    if (this.hasAccountAccess) {
      this.getAccounts();
      this.searchForm.get('status')?.setValue(['RECIBIDO', 'PALETIZADO', 'COMPLETADO']);
    } else {
      this.getAccount();
      this.searchForm.get('status')?.setValue(['RECIBIDO', 'PALETIZADO']);
    }
  }

  getAccounts() {
    this.accountLoading = true;
    this.accountService.searchAllAccounts(this.searchAccountQuery).subscribe((response: PageableAccount) => {
      this.accounts = response.content;
      this.accountLoading = false;
    });
  }

  getAccount() {
    this.searchForm.get('accountId')?.disable();
    this.accountLoading = true;
    this.userAccount = this.authService.getUserAccount();
    this.searchForm.patchValue({ accountId: this.userAccount.accountId });
    this.accountService.getDropdownAccount(this.userAccount.accountId).subscribe((response: AccountDropdown[]) => {
      this.accounts = response;
      this.accountLoading = false;
    });
  }

  loadItems(event: any) {
    this.loading = true;

    const sortField = event.sortField ?? 'itemId';
    const sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';

    const params: ItemSearchParams = {
      ...this.searchForm.value,
      creationDateFrom: this.formatDate(this.searchForm.value.creationDateFrom),
      creationDateTo: this.formatDate(this.searchForm.value.creationDateTo),
      page: event.first / event.rows,
      size: event.rows,
      sortBy: sortField,
      sortDirection: sortOrder,
      warehouse: this.searchForm.get('warehouse')?.value,
      accountId: this.searchForm.get('accountId')?.value
    };

    this.itemService.searchItems(params).subscribe((data) => {
      this.items = data.content;
      this.totalRecords = data.totalElements;
      this.totalStock = this.items.reduce((sum, item) => sum + item.quantity, 0);
      this.totalUnits = this.items.reduce((sum, item) => sum + item.initialQuantity, 0);
      this.loading = false;
    });
  }

  private formatDate(date: Date | null): string | null {
    return date ? date.toISOString() : null;
  }

  getSeverity(status: string) {
    switch (status) {
      case 'COMPLETADO':
        return 'success';
      case 'PALETIZADO':
        return 'info';
      case 'RECIBIDO':
        return 'warn';
      case 'ELIMINADO':
        return 'danger';
      default:
        return undefined;
    }
  }

  resetForm() {
    this.searchForm.reset();
    this.searchForm.patchValue({ warehouse: this.warehouses[0].name });
    this.searchForm.patchValue({ accountId: this.userAccount.accountId });
    this.search();
    //this.items = [];
  }

  showHistory(item: Item) {
    this.selectedItem = item;
    this.itemService.getItemMovements(item.itemId).subscribe(data => {
      this.movements = data.map(m => ({
        ...m,
        changeDetail:
          typeof m.changeDetail === 'string'
            ? JSON.parse(m.changeDetail)
            : m.changeDetail
      }));
      if (data.length > 0) this.historyDialogVisible = true;
    });
  }

  deleteItem(item: Item) {
    this.confirmationService.confirm({
      message: 'Estas seguro que quieres eliminar el artículo ' + item.code + '?',
      header: 'Confirmación',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.itemService.deleteItem(item.itemId).subscribe({
          next: () => {
            item.status = 'ELIMINADO';
            this.messageService.add({ severity: 'success', summary: 'Exitoso', detail: 'Artículo eliminado', life: 5000 });
          },
          error: (error) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.message ?? 'No se pudo eliminar el artículo' });
          }
        });
      }
    });
  }

  getFieldLabel(key: string): string {
    return this.itemService.formatFieldName(key);
  }

  getFieldValue(_key: string, value: any): string {
    return this.itemService.formatFieldValue(value);
  }

  initColumns() {
    const roleFields = this.ROLE_COLUMNS[this.currentRole] ?? [];
    const saved = this.colPrefService.load(this.currentUserId, 'ArticlesInventory');

    this.columnsToggleList = this.ALL_COLUMNS.filter(c => roleFields.includes(c.field));

    let activeFields: string[];

    if (saved) {
      activeFields = this.colPrefService.reconcile(saved, roleFields);
    } else {
      activeFields = roleFields.filter(f => {
        const col = this.ALL_COLUMNS.find(c => c.field === f);
        return col && !col.defaultHidden;
      });
    }

    this.columnsToggleList.forEach(c => {
      c.visible = c.alwaysVisible ? true : activeFields.includes(c.field);
    });

    this.applyColumns(activeFields);
  }

  applyColumns(fields: string[]) {
    const alwaysVisible = this.columnsToggleList.filter(c => c.alwaysVisible).map(c => c.field);
    const merged = [...new Set([...alwaysVisible, ...fields])];
    this.appliedTableColumns = this.columnsToggleList.filter(c => merged.includes(c.field));
  }

  onSaveColumns() {
    const selected = this.columnsToggleList.filter(c => c.visible || c.alwaysVisible).map(c => c.field);
    this.colPrefService.save(this.currentUserId, 'ArticlesInventory', selected);
    this.applyColumns(selected);
    //this.overlayPanel.hide();
  }

  onClearColumns() {
    this.columnsToggleList.forEach(c => c.visible = c.alwaysVisible ?? false);
    this.selectAll = false;
    this.applyColumns([]);
  }

  get filteredColumns(): ColumnConfig[] {
    if (!this.searchTerm.trim()) return this.columnsToggleList;
    return this.columnsToggleList.filter(c =>
      c.header.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  onSelectAll(event: any) {
    this.columnsToggleList.forEach(c => {
      if (!c.alwaysVisible) c.visible = event.checked;
    });
  }

  onColumnToggle() {
    const toggleable = this.columnsToggleList.filter(c => !c.alwaysVisible);
    this.selectAll = toggleable.every(c => c.visible);
  }

  resolveField(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  exportExcel() {
    const summary = `Total de Recibidos: ${this.totalUnits} | Total en Stock: ${this.totalStock}`;
    this.exportService.export(this.items, this.appliedTableColumns, 'inventario_articulos', summary);
  }

}
